import { DOCUMENT, isPlatformBrowser } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  PLATFORM_ID,
} from "@angular/core";
import { NavigationEnd, Router, RouterOutlet } from "@angular/router";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { filter, map, startWith } from "rxjs";
import { AppHeaderComponent } from "./layout/app-header.component";
import { AppFooterComponent } from "./layout/app-footer.component";

@Component({
  selector: "app-root",
  standalone: true,
  imports: [RouterOutlet, AppHeaderComponent, AppFooterComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./app.component.html",
  styleUrl: "./app.component.scss",
})
export class AppComponent {
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.router.events
        .pipe(
          filter((event) => event instanceof NavigationEnd),
          takeUntilDestroyed(),
        )
        .subscribe(() => this.document.defaultView?.scrollTo({ top: 0 }));
    }
  }

  private readonly currentPath = (): string => {
    const pathname =
      this.document.defaultView?.location.pathname ?? this.router.url;
    return pathname.split(/[?#]/, 1)[0] ?? "/";
  };

  readonly isImmersiveRoute = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
      startWith(this.currentPath()),
      map((url) =>
        /(^|\/)(?:chat|video)(?:\/|$)/.test(url.split(/[?#]/, 1)[0]),
      ),
    ),
    {
      initialValue: /(^|\/)(?:chat|video)(?:\/|$)/.test(
        this.currentPath().split(/[?#]/, 1)[0],
      ),
    },
  );
}
